import React, { type FC } from 'react'
import { AlertDialog, Button } from '@awell-health/design-system'
import { OrganizationSwitcher } from '../OrganizationSwitcher'
import { environment } from '../environment'
import { navigate } from '@reach/router'
import { useStytchB2BClient } from '@stytch/react/b2b'
import { useStytchB2BClient as useStytchB2BClientNext } from '@stytch/nextjs/b2b'

interface AuthorizationErrorDialogProps {
  isOpen: boolean
  onClose: () => void
  onOrganizationSwitch: () => void
}

export const AuthorizationErrorDialog: FC<AuthorizationErrorDialogProps> = ({
  isOpen,
  onClose,
  onOrganizationSwitch
}) => {
  const [showOrganizationSwitcher, setShowOrganizationSwitcher] = React.useState(false)
  let stytch: ReturnType<typeof useStytchB2BClient>

  try {
    stytch = useStytchB2BClient()
  } catch (err) {
    stytch = useStytchB2BClientNext()
  }

  const handleReauthenticate = async () => {
    // Revoke session and redirect to login
    await stytch.session.revoke()
    void navigate(environment.urls.careops + '/session-expired')
  }

  const handleOrganizationSwitch = () => {
    setShowOrganizationSwitcher(true)
  }

  const handleSwitchComplete = () => {
    setShowOrganizationSwitcher(false)
    onOrganizationSwitch()
    onClose()
  }

  if (!isOpen) {
    return null
  }

  return (
    <AlertDialog
      title="Access Denied"
      onClose={() => {
        // Only allow closing if not showing organization switcher
        if (!showOrganizationSwitcher) {
          onClose()
        }
      }}
    >
      {!showOrganizationSwitcher ? (
        <div className="space-y-4">
          <p className="text-gray-700">
            The page you are trying to access either does not exist or belongs to a different organization.
          </p>

          <div className="flex gap-3 justify-end pt-4">
            <Button variant="secondary" onClick={handleOrganizationSwitch}>
              Switch Organization
            </Button>
            <Button variant="primary" onClick={handleReauthenticate}>
              Re-authenticate
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-gray-600 mb-4">Select an organization to switch to:</p>
          <OrganizationSwitcher environment={environment.environment} onSwitch={handleSwitchComplete} mode="inline" />
          <div className="flex justify-end pt-4">
            <Button variant="tertiary" onClick={() => setShowOrganizationSwitcher(false)}>
              Back
            </Button>
          </div>
        </div>
      )}
    </AlertDialog>
  )
}
